import React, { useState, useEffect, useRef } from 'react';
import { sheetsClient } from '../../data/sheetsClient';
import { useTeacherData } from '../../context/TeacherDataContext';
import { Card, Button, Skeleton } from '../shared/UI';

export default function SchoolProfile() {
  const topRef = useRef(null);
  const { school, loading: { school: schoolLoading }, refetch } = useTeacherData();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!school) return;
    setForm({
      schoolAddress: school.address || '',
      city: school.city || '',
      province: school.province || '',
      postalCode: school.postalCode || '',
      schoolBoard: school.schoolBoard || '',
      principalName: school.principalName || '',
      principalContact: school.principalContact || '',
      teacherName: school.teacherName || '',
      position: school.position || '',
      registrantEmail: school.registrantEmail || '',
      alternateEmail: school.alternateEmail || '',
      cellPhone: school.cellPhone || '',
      website: school.website || '',
      instagram: school.instagram || '',
      tiktok: school.tiktok || '',
    });
  }, [school]);

  const set = (key) => (value) => { setForm(prev => ({ ...prev, [key]: value })); setSaved(false); };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await sheetsClient.updateSchool(form);
      await refetch.school();
      setSaved(true);
      topRef.current?.scrollIntoView({ behavior: 'instant' });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (schoolLoading || !form) {
    return (
      <div style={{ padding: 24, maxWidth: 680 }}>
        <Skeleton width={200} height={26} style={{ marginBottom: 10 }} />
        <Skeleton width={320} height={14} style={{ marginBottom: 20 }} />
        {['School', 'Principal', 'Primary Contact', 'Social Media'].map((title) => (
          <Card key={title} title={title}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Skeleton height={38} radius={8} />
              <Skeleton height={38} radius={8} />
            </div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div ref={topRef} style={{ padding: 24, maxWidth: 680 }}>
      <h1 style={{ fontSize: 22, fontWeight: 500, marginBottom: 4 }}>School Profile</h1>
      <p style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>
        Update your school's contact and administrative information.
      </p>

      {error && (
        <div style={{ background: '#FCEBEB', border: '1px solid #F3C9C9', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#A32D2D', marginBottom: 16 }}>
          {error}
        </div>
      )}
      {saved && (
        <div style={{ background: '#E6F7F1', border: '1px solid #A3D9C2', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#1D7A59', marginBottom: 16 }}>
          Profile saved successfully.
        </div>
      )}

      <Card title="School">
        <div style={{ background: '#F5F5F5', borderRadius: 8, padding: '9px 13px', marginBottom: 14, fontSize: 13, color: '#555' }}>
          <strong>School name:</strong> {school.name}
          <span style={{ marginLeft: 8, color: '#999', fontSize: 12 }}>Contact the admin to change your school name.</span>
        </div>
        <Field label="Address" value={form.schoolAddress} onChange={set('schoolAddress')} />
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
          <Field label="City" value={form.city} onChange={set('city')} />
          <Field label="Province" value={form.province} onChange={set('province')} />
          <Field label="Postal Code" value={form.postalCode} onChange={set('postalCode')} />
        </div>
        <Field label="School Board" value={form.schoolBoard} onChange={set('schoolBoard')} />
      </Card>

      <Card title="Principal">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Principal Name" value={form.principalName} onChange={set('principalName')} />
          <Field label="Principal Contact" value={form.principalContact} onChange={set('principalContact')} />
        </div>
      </Card>

      <Card title="Primary Contact">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Name" value={form.teacherName} onChange={set('teacherName')} />
          <SelectField label="Position" value={form.position} onChange={set('position')} options={[
            'Classroom Teacher',
            'Administrator',
            'Dance Coach',
            'Parent/Homeschool Representative',
          ]} />
          <Field label="Email" value={form.registrantEmail} onChange={set('registrantEmail')} type="email" />
          <Field label="Alternate Email" value={form.alternateEmail} onChange={set('alternateEmail')} type="email" />
          <Field label="Cell Phone" value={form.cellPhone} onChange={set('cellPhone')} type="tel" />
        </div>
      </Card>

      <Card title="Social Media">
        <Field label="Website" value={form.website} onChange={set('website')} type="url" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Instagram" value={form.instagram} onChange={set('instagram')} />
          <Field label="TikTok" value={form.tiktok} onChange={set('tiktok')} />
        </div>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 13, color: '#666', marginBottom: 5, display: 'block' }}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%', padding: '8px 12px', border: '1px solid #D5D5D5',
          borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 13, color: '#666', marginBottom: 5, display: 'block' }}>{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%', padding: '8px 12px', border: '1px solid #D5D5D5',
          borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box',
          background: '#fff', appearance: 'auto',
        }}
      >
        <option value="">— Select —</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
